import { CSSProperties, type MouseEvent } from 'react';
import { FilterFn, Updater } from '@tanstack/react-table';
import { isEmpty, isNil, toString, trim } from 'lodash-es';
import moment from 'moment';
import {
  parseMomentUtc,
  getLocalTimezoneAbbreviation,
  US_DATE_FORMAT,
  US_DATETIME_FORMAT,
} from 'utils/dates';
import { formatNumber } from 'utils/number';
import { ColumnFormat, ColumnType, DataTableColumnMeta } from './types';

// ─────────────────────────────────────
// Reserved Column IDs + Size
// ─────────────────────────────────────

/**
 * DataTable auto-injects these columns when their respective features are
 * enabled. The IDs are referenced both by the column factories (in
 * `data-table.tsx`) and by `useTableState` (for default column
 * order / sizing), so they live in one place.
 */
export const RESERVED_COLUMN_IDS = {
  SELECT: '_select',
  EXPANDER: '_expander',
} as const;

/** Width (px) of the auto-injected `_select` / `_expander` columns. */
export const RESERVED_COLUMN_SIZE = 48;

// Titles plain text that is cut off; element content sets its own title.
export const titleFromText = (event: MouseEvent<HTMLElement>) => {
  const el = event.currentTarget;
  const nodes = Array.from(el.childNodes);
  if (
    nodes.length &&
    nodes.every((node) => node.nodeType === Node.TEXT_NODE) &&
    el.scrollWidth > el.clientWidth
  )
    el.title = el.textContent ?? '';
  else el.removeAttribute('title');
};

/**
 * Titles a stacked cell with all its values when any is cut off or below the row.
 * @param {MouseEvent<HTMLElement>} event - The mouseenter event on the stack
 */
export const titleFromStack = (event: MouseEvent<HTMLElement>) => {
  const el = event.currentTarget;
  const items = Array.from(el.children) as HTMLElement[];
  const clipped =
    el.offsetHeight > (el.parentElement?.clientHeight ?? Infinity) ||
    items.some((item) => item.scrollWidth > item.clientWidth);
  if (clipped) el.title = items.map((item) => item.textContent).join('\n');
  else el.removeAttribute('title');
};

// ─────────────────────────────────────
// Cell Value Formatting
// ─────────────────────────────────────

/**
 * Format a cell value based on column metadata type and format options.
 * Used for both display rendering and export.
 * @param {unknown} value - The raw cell value
 * @param {DataTableColumnMeta} meta - Column metadata with type and format options
 * @return {string} The formatted string value
 */
export function formatCellValue(
  value: unknown,
  meta?: DataTableColumnMeta
): string {
  // Handle null/undefined
  if (isNil(value)) return '—';

  // For strings: normalize whitespace and check empty
  if (typeof value === 'string') {
    const cleaned = trim(value.replace(/\s+/g, ' '));
    if (isEmpty(cleaned)) return '—';
    value = cleaned;
  }

  // No type specified - return as string
  if (!meta?.type) return toString(value);

  // Get format options (optional customization)
  const format = meta.format ?? {};

  switch (meta.type) {
    case 'integer': {
      const { multiplier = 1, unit = '' } = format;
      return (
        formatNumber({
          value: Number(value) * multiplier,
          minPrecision: 0,
          maxPrecision: 0,
          includeCommas: true,
        }) + unit
      );
    }

    case 'decimal': {
      const {
        minDecimalPlaces = 0,
        maxDecimalPlaces = 2,
        multiplier = 1,
        unit = '',
      } = format;
      return (
        formatNumber({
          value: Number(value) * multiplier,
          minPrecision: minDecimalPlaces,
          maxPrecision: maxDecimalPlaces,
          includeCommas: true,
        }) + unit
      );
    }

    case 'currency': {
      const { minDecimalPlaces = 2, maxDecimalPlaces = 2 } = format;
      return formatNumber({
        value: Number(value),
        minPrecision: minDecimalPlaces,
        maxPrecision: maxDecimalPlaces,
        style: 'currency',
        includeCommas: true,
      });
    }

    case 'percent': {
      const {
        minDecimalPlaces = 0,
        maxDecimalPlaces = 0,
        multiplier = 1,
      } = format;
      return (
        formatNumber({
          value: Number(value) * multiplier,
          minPrecision: minDecimalPlaces,
          maxPrecision: maxDecimalPlaces,
          includeCommas: true,
        }) + '%'
      );
    }

    case 'compact': {
      const { multiplier = 1, unit = '' } = format;
      return (
        new Intl.NumberFormat('en-US', {
          notation: 'compact',
        }).format(Number(value) * multiplier) + unit
      );
    }

    case 'date':
      return parseMomentUtc(value as string).format(US_DATE_FORMAT);

    case 'datetime':
      return `${parseMomentUtc(value as string)
        .local()
        .format(US_DATETIME_FORMAT)} ${getLocalTimezoneAbbreviation()}`;

    case 'duration': {
      // moment.duration accepts seconds (number), ISO 8601 strings ("PT1H30M"), etc.
      let dur: moment.Duration;
      if (typeof value === 'number') {
        dur = moment.duration(value, 'seconds');
      } else if (typeof value === 'string' && /^P/i.test(value)) {
        dur = moment.duration(value);
      } else {
        // Try parsing as a number of seconds
        const num = Number(value);
        if (isNaN(num)) return '—';
        dur = moment.duration(num, 'seconds');
      }
      const h = Math.floor(dur.asHours());
      const m = dur.minutes();
      const s = dur.seconds() + dur.milliseconds() / 1000;
      const sPart = `${formatNumber(s, 2, 2, 'decimal', false)}s`;
      if (h > 0) return `${h}h ${m}m ${sPart}`;
      if (m > 0) return `${m}m ${sPart}`;
      return sPart;
    }

    case 'boolean':
      return value ? 'true' : 'false';

    case 'text':
    default:
      return toString(value);
  }
}

// Spread into a column def: its id and accessor, a missing value sorting last whichever way the column sorts.
export const blanksLast = <Row>(key: keyof Row & string) => ({
  id: key,
  accessorFn: (row: Row) => row[key] ?? undefined,
  sortUndefined: 'last' as const,
});

// ─────────────────────────────────────
// Filter Functions
// ─────────────────────────────────────

/**
 * Filter function for single-select filters (exact string match)
 * @param {Row} row - The row to filter
 * @param {string} columnId - The column ID to filter on
 * @param {string} filterValue - The filter value to match
 * @return {boolean} Whether the row passes the filter
 */
export const selectFilter: FilterFn<unknown> = (
  row,
  columnId,
  filterValue: string
) => {
  if (!filterValue) return true;
  const cellValue = String(row.getValue(columnId));
  return cellValue === filterValue;
};

selectFilter.autoRemove = (val: string) => !val;

/**
 * Filter function for multi-select filters (array of selected values)
 * @param {Row} row - The row to filter
 * @param {string} columnId - The column ID to filter on
 * @param {string[]} filterValue - Array of values to match
 * @return {boolean} Whether the row passes the filter
 */
export const multiSelectFilter: FilterFn<unknown> = (
  row,
  columnId,
  filterValue: string[]
) => {
  if (!filterValue || filterValue.length === 0) return true;
  const cellValue = String(row.getValue(columnId));
  return filterValue.includes(cellValue);
};

multiSelectFilter.autoRemove = (val: string[]) => !val || val.length === 0;

/**
 * Filter function for numeric range filters [min, max]
 * @param {Row} row - The row to filter
 * @param {string} columnId - The column ID to filter on
 * @param {Array} filterValue - Tuple of [min, max] values
 * @return {boolean} Whether the row passes the filter
 */
export const numberRangeFilter: FilterFn<unknown> = (
  row,
  columnId,
  filterValue: [number | null, number | null]
) => {
  const [min, max] = filterValue ?? [null, null];
  if (min === null && max === null) return true;

  const cellValue = row.getValue(columnId);
  if (cellValue === null || cellValue === undefined) return false;

  const numValue = Number(cellValue);
  if (isNaN(numValue)) return false;

  if (min !== null && numValue < min) return false;
  if (max !== null && numValue > max) return false;

  return true;
};

numberRangeFilter.autoRemove = (val: [number | null, number | null]) =>
  !val || (val[0] === null && val[1] === null);

/**
 * Creates a number range filter that respects the multiplier format option.
 * Filter values are divided by multiplier before comparison, so users can
 * filter using displayed values (e.g., type "30" for 30% when data is 0.30).
 * @param {number} multiplier - The multiplier to apply (default 1)
 * @return {FilterFn} The filter function
 */
export function createNumberRangeFilter(
  multiplier: number = 1
): FilterFn<unknown> {
  const filterFn: FilterFn<unknown> = (
    row,
    columnId,
    filterValue: [number | null, number | null]
  ) => {
    const [min, max] = filterValue ?? [null, null];
    if (min === null && max === null) return true;

    const cellValue = row.getValue(columnId);
    if (cellValue === null || cellValue === undefined) return false;

    const numValue = Number(cellValue);
    if (isNaN(numValue)) return false;

    // Apply inverse multiplier to filter values
    const adjustedMin = min !== null ? min / multiplier : null;
    const adjustedMax = max !== null ? max / multiplier : null;

    if (adjustedMin !== null && numValue < adjustedMin) return false;
    if (adjustedMax !== null && numValue > adjustedMax) return false;

    return true;
  };

  filterFn.autoRemove = (val: [number | null, number | null]) =>
    !val || (val[0] === null && val[1] === null);

  return filterFn;
}

/**
 * Filter function for date range filters [start, end]
 * Uses moment.js for consistent date parsing and comparison
 * @param {Row} row - The row to filter
 * @param {string} columnId - The column ID to filter on
 * @param {Array} filterValue - Tuple of [start, end] ISO date strings
 * @return {boolean} Whether the row passes the filter
 */
export const dateRangeFilter: FilterFn<unknown> = (
  row,
  columnId,
  filterValue: [string | null, string | null]
) => {
  const [start, end] = filterValue ?? [null, null];
  if (!start && !end) return true;

  const cellValue = row.getValue(columnId);
  if (cellValue === null || cellValue === undefined) return false;

  const cellDate = parseMomentUtc(cellValue as string);
  if (!cellDate.isValid()) return false;

  if (start) {
    const startDate = parseMomentUtc(start);
    if (cellDate.isBefore(startDate, 'day')) return false;
  }

  if (end) {
    const endDate = parseMomentUtc(end).endOf('day');
    if (cellDate.isAfter(endDate)) return false;
  }

  return true;
};

dateRangeFilter.autoRemove = (val: [string | null, string | null]) =>
  !val || (!val[0] && !val[1]);

/**
 * Get the appropriate filter function based on column type and format
 * @param {ColumnType} type - The column type
 * @param {ColumnFormat} format - Optional format configuration
 * @return {FilterFn} The appropriate filter function
 */
export function getFilterFnForType(
  type: ColumnType | undefined,
  format?: ColumnFormat
): FilterFn<unknown> {
  switch (type) {
    case 'integer':
    case 'decimal':
    case 'currency':
    case 'compact':
    case 'duration':
      return createNumberRangeFilter(format?.multiplier ?? 1);
    case 'percent':
      return createNumberRangeFilter(format?.multiplier ?? 1);
    case 'date':
    case 'datetime':
      return dateRangeFilter;
    case 'boolean':
    case 'enum':
      return multiSelectFilter;
    case 'text':
    default:
      return 'includesString' as unknown as FilterFn<unknown>;
  }
}

// ─────────────────────────────────────
// Column Filter Validation
// ─────────────────────────────────────

const COLUMN_FILTER_COMPATIBILITY: Record<string, ColumnType[]> = {
  text: ['text'],
  range: ['integer', 'decimal', 'currency', 'percent', 'compact', 'duration'],
  date: ['date', 'datetime'],
  radio: ['boolean', 'enum'],
  select: ['text', 'boolean', 'enum'],
  multiSelect: ['text', 'boolean', 'enum'],
  checkboxGroup: ['boolean', 'enum'],
};

/**
 * Validates that a column-bound filter is compatible with the column type.
 * Throws an error if the filter type doesn't match the column type.
 * @param {string} filterType - The filter type (text, range, date, select, multiSelect)
 * @param {ColumnType} columnType - The column's type from meta
 * @param {string} columnId - The column ID for error messaging
 */
export function validateColumnFilterType(
  filterType: keyof typeof COLUMN_FILTER_COMPATIBILITY,
  columnType: ColumnType | undefined,
  columnId: string
): void {
  // For radio/select/multiSelect/checkboxGroup, require explicit enum or
  // boolean type because they use multiSelectFilter which expects array values
  if (
    (filterType === 'radio' ||
      filterType === 'select' ||
      filterType === 'multiSelect' ||
      filterType === 'checkboxGroup') &&
    columnType !== 'boolean' &&
    columnType !== 'enum'
  ) {
    throw new Error(
      `Column "${columnId}" uses ${filterType} filter but has type "${
        columnType ?? 'undefined'
      }". ` +
        `Set meta.type to 'enum' or 'boolean' for select/multiSelect/checkboxGroup filters.`
    );
  }

  const allowedTypes = COLUMN_FILTER_COMPATIBILITY[filterType];
  const actualType = columnType ?? 'text';

  if (!allowedTypes.includes(actualType)) {
    throw new Error(
      `Column "${columnId}" has type "${actualType}" but is used with ` +
        `${filterType} filter which only supports: ${allowedTypes.join(', ')}`
    );
  }
}

// ─────────────────────────────────────
// Column Pinning Styles
// ─────────────────────────────────────

/** Subset of TanStack column methods needed for pinning styles. */
type ColumnWithPinning = {
  getIsPinned: () => false | 'left' | 'right';
  getIsLastColumn: (position: 'left' | 'right') => boolean;
  getIsFirstColumn: (position: 'left' | 'right') => boolean;
  getStart: (position: 'left' | 'right') => number;
  getAfter: (position: 'left' | 'right') => number;
  getSize: () => number;
};

/**
 * Returns CSS styles for pinned columns (sticky positioning, shadows).
 * @param {ColumnWithPinning} column - The column to get pinning styles for.
 * @return {CSSProperties} CSS properties for pinned column styling.
 */
export const getPinningStyles = (column: ColumnWithPinning): CSSProperties => {
  const isPinned = column.getIsPinned();
  const isLastLeftPinnedColumn =
    isPinned === 'left' && column.getIsLastColumn('left');
  const isFirstRightPinnedColumn =
    isPinned === 'right' && column.getIsFirstColumn('right');

  return {
    boxShadow: isFirstRightPinnedColumn
      ? 'inset 5px 0px 6px -6px rgba(141,141,141,1)'
      : isLastLeftPinnedColumn
        ? 'inset -5px 0px 6px -6px rgba(141,141,141,1)'
        : undefined,
    left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
    right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
    position: isPinned ? 'sticky' : 'relative',
    zIndex: isPinned ? 1 : 0,
  };
};

// ─────────────────────────────────────
// Cell Alignment
// ─────────────────────────────────────

/**
 * Get cell alignment based on column type or explicit override
 * @param {DataTableColumnMeta} meta - Column metadata
 * @return {'left' | 'center' | 'right'} The alignment value
 */
export function getAlignment(
  meta?: DataTableColumnMeta
): 'left' | 'center' | 'right' {
  // Explicit override takes precedence
  if (meta?.align) return meta.align;

  // Auto-align based on type
  switch (meta?.type) {
    case 'integer':
    case 'decimal':
    case 'currency':
    case 'percent':
    case 'compact':
    case 'duration':
      return 'right';
    case 'boolean':
      return 'center';
    case 'text':
    case 'date':
    case 'datetime':
    default:
      return 'left';
  }
}

// ─────────────────────────────────────
// Filter Variant Detection
// ─────────────────────────────────────

export type FilterVariant =
  'text' | 'range' | 'select' | 'multiselect' | 'date' | 'none';

/**
 * Get filter variant based on column type
 * @param {DataTableColumnMeta} meta - Column metadata
 * @return {FilterVariant} The filter variant to use
 */
export function getFilterVariant(meta?: DataTableColumnMeta): FilterVariant {
  switch (meta?.type) {
    case 'integer':
    case 'decimal':
    case 'currency':
    case 'percent':
    case 'compact':
    case 'duration':
      return 'range';
    case 'date':
    case 'datetime':
      return 'date';
    case 'boolean':
      return 'multiselect';
    case 'enum':
      return 'multiselect';
    case 'text':
    default:
      return 'text';
  }
}

/**
 * Yes/No filter options for a boolean column: the option VALUES are the wire
 * `'true'`/`'false'` the multi-select filter sends, the LABELS are what the user reads.
 * Shared so a boolean column's `meta.filterOptions` can't drift between views (Properties,
 * Property Allocations, and the DCA well-jobs "Latest" column). Not `as const` — a readonly
 * tuple isn't assignable to `filterOptions?: { value: string; label: string }[]`.
 */
export const BOOLEAN_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

// ─────────────────────────────────────
// Updater Helper
// ─────────────────────────────────────

/**
 * Resolves TanStack Table's Updater<T> pattern to a concrete value
 * @param {Updater<T>} updaterOrValue - The updater function or value
 * @param {T} previousValue - The previous value
 * @return {T} The resolved value
 */
export function resolveUpdater<T>(
  updaterOrValue: Updater<T>,
  previousValue: T
): T {
  return typeof updaterOrValue === 'function'
    ? (updaterOrValue as (prev: T) => T)(previousValue)
    : updaterOrValue;
}
